import { useEffect, useState } from "react";
import ApiTable from "../../../@ui/ApiTable/ApiTable";
import { ApiTableColumn } from "../../../types/ApiTable/ApiTableColumn";
import { ApiTableState } from "../../../types/ApiTable/ApiTableState";
import { UserDetails } from "../../../types/UserAdmin/UserDetails";
import { Pagination } from "../../../types/PaginiationResult";
import { FilterType } from "../../../types/ApiTable/ColumnFilter";
import TableActionButton from "../../../@ui/TableActionButton";
import { useNavigate } from "react-router-dom";
import { LicenceType } from "../../../types/Licences/LicenceType";
import {
  useLicenceTypeAdminService,
  useUserAdminService,
} from "../../../hooks";
import UserOverview from "./UserOverview";
import { Button } from "react-bootstrap";
// import { mkConfig, generateCsv, download } from "export-to-csv/output/index.js";
import Papa from "papaparse";

const Users = () => {
  const navigate = useNavigate();
  const userAdminService = useUserAdminService();
  const licenceTypeAdminService = useLicenceTypeAdminService();
  const [licenceTypes, setLicenceTypes] = useState<LicenceType[]>([]);
  const [tableParams, setTableParams] = useState<ApiTableState>({
    pagination: {
      page: 1,
      pageSize: 20,
    },
  });
  const [users, setUsers] = useState<Pagination<UserDetails>>({
    records: [],
    totalPages: 0,
    page: 1,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    licenceTypeAdminService
      .GetLicenceSummaries()
      .then((response) => setLicenceTypes(response.licenceTypes))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    userAdminService
      .listUsers(tableParams, abortController)
      .then((response) => {
        setUsers(response);
        setLoading(false);
      })
      .catch(() => {});

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableParams]);

  const cols = [
    {
      field: "userId",
      label: "Name",
      renderContents: (value: UserDetails) => (
        <>{`${value.firstName} ${value.lastName}`}</>
      ),
      sortable: true,
      style: { width: "20%" },
      filter: {
        type: FilterType.TYPEAHEAD,
        typeaheadOptions: {
          optionLabelKey: "fullName",
          optionValueKey: "userId",
          onSearch: (searchTerm: string) =>
            userAdminService.searchUsers(searchTerm),
        },
      },
    },
    {
      field: "email",
      label: "Email",
      renderContents: (value: UserDetails) => <>{value.email}</>,
      sortable: true,
      style: { width: "30%" },
      filter: {
        type: FilterType.TYPEAHEAD,
        typeaheadOptions: {
          optionLabelKey: "email",
          optionValueKey: "email",
          onSearch: (searchTerm: string) =>
            userAdminService.searchUsers(searchTerm),
        },
      },
    },
    {
      field: "registered",
      label: "Registered",
      sortable: true,
      renderContents: (value: UserDetails) => (
        <>{value.registered.toFormat("dd/MM/yyyy")}</>
      ),
      style: { width: "10%" },
    },
    {
      field: "lastSeen",
      label: "Last Seen",
      sortable: false,
      renderContents: (value: UserDetails) => (
        <>{value.lastSeen ? value.lastSeen.toFormat("dd/MM/yyyy") : "-"}</>
      ),
      style: { width: "10%" },
    },
    {
      field: "licence",
      label: "Product Licence",
      sortable: false,
      renderContents: (value: UserDetails) => <>{value.licence}</>,
      style: { width: "10%" },
      filter: {
        filterKey: "licences.LicenceName",
        type: FilterType.SELECT,
        selectOptions: {
          labelKey: "name",
          valueKey: "name",
          options: licenceTypes,
        },
      },
    },
    {
      field: "licenceExpiry",
      label: "Licence Expiry",
      sortable: false,
      renderContents: (value: UserDetails) => (
        <>
          {value.licenceExpiry ?
            value.licenceExpiry.toFormat("dd/MM/yyyy")
          : "-"}
        </>
      ),
      style: { width: "10%" },
    },
    {
      field: "organisation",
      label: "Organisation",
      sortable: true,
      renderContents: (value: UserDetails) => <>{value.organisation}</>,
      style: { width: "10%" },
      filter: {
        type: FilterType.TYPEAHEAD,
        filterKey: "OrganisationName",
        typeaheadOptions: {
          optionValueKey: "name",
          optionLabelKey: "name",
          onSearch: (searchTerm: string) =>
            userAdminService.searchOrganisations(searchTerm),
        },
      },
    },
    {
      field: "",
      label: "",
      renderContents: (value: UserDetails) => (
        <TableActionButton onClick={() => handleSelect(value)}>
          Logs
        </TableActionButton>
      ),
      style: { width: "1%" },
    },
  ] as ApiTableColumn[];

  const handleSelect = (details: UserDetails) => {
    navigate(`/admin/request-history?userId=${details.userId}`);
  };
  const downloadUsers = () => {
    const abortController = new AbortController();
    setLoading(true);

    const downloadParams = {
      sort: tableParams.sort,
      filters: tableParams.filters,
      filterExpression: tableParams.filterExpression,
      pagination: { page: 1, pageSize: 20000 },
    };

    userAdminService
      .listUsers(downloadParams, abortController)
      .then((response) => {
        const csv = Papa.unparse(response.records);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "users.csv";
        link.click();
        URL.revokeObjectURL(url);
        setLoading(false);
      })
      .catch(() => setLoading(false))
      .finally(() => abortController.abort());
  };
  return (
    <>
      <UserOverview />
      <div className="mt-4 d-flex justify-content-end">
        <Button onClick={downloadUsers}>Download</Button>
      </div>
      <ApiTable
        data={users}
        columns={cols}
        loading={loading}
        onChange={(state) => setTableParams(state)}
        state={tableParams}
        pageOptions={[10, 20, 50]}
      />
    </>
  );
};

export default Users;
